import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { formatApiError } from "../context/AuthContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { STAGE_LABELS, STAGES, SOURCE_LABELS, TARGET_LABELS, ACTION_LABELS, LEVEL_META, computeLevel } from "../lib/constants";
import { LevelBadge } from "../components/LevelBadge";
import LeadAttachments from "../components/LeadAttachments";
import LeadAnalysis from "../components/LeadAnalysis";
import RoofMap from "../components/RoofMap";
import PreOutlookShare from "../components/PreOutlookShare";
import LeadPreventivo from "../components/LeadPreventivo";
import EvBenefitEditor from "../components/EvBenefitEditor";
import LeadOutlook from "../components/LeadOutlook";
import PreFattibilita from "../components/PreFattibilita";
import LeadAnagrafica from "../components/LeadAnagrafica";
import TechnicalReview from "../components/TechnicalReview";
import PartnerAssignment from "../components/PartnerAssignment";
import AutomationLeadPanel from "../components/AutomationLeadPanel";
import { PrivacyLeadPanel } from "../components/PrivacyLeadPanel";
import QualityWarningDialog from "../components/QualityWarningDialog";
import { AssignInstallersDialog } from "../components/AssignInstallersDialog";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { ArrowLeft, Phone, Mail, MapPin, Loader2, Save, Trash2, Plus, Clock, Zap, PiggyBank, Wrench, Target, PhoneCall, MessageSquare, FileText, Handshake, FileSignature, StickyNote, UserCheck, CalendarClock, Cog, FolderPlus, Rocket, Lock, Building2, Hash, Skull, Undo2, Star, HardHat, UserPlus, Contact } from "lucide-react";

function ProjectGate({ variant, name, isProject = true, ownerSet, contactSet, reviewed }) {
  const required = variant === "required";
  const missing = [];
  if (required && isProject && !ownerSet) missing.push("il Referente di progetto");
  if (required && isProject && ownerSet && !reviewed) missing.push("la Revisione tecnica confermata (scheda \"Revisione\")");
  return (
    <div className={`rounded border p-4 mb-4 flex items-start gap-3 ${required ? "border-primary/40 bg-primary/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="project-gate-banner">
      {required ? <Rocket className="h-5 w-5 text-primary shrink-0 mt-0.5" /> : <Lock className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />}
      <div className="text-sm">
        {required ? (
          <>Prima di generare il preventivo, imposta nel pannello a sinistra: <b>{missing.join(" e ")}</b>.{isProject ? " Chi imposta il referente di progetto ne diventa il responsabile e sarà l'unico (con gli admin) a poter sviluppare i passaggi successivi." : ""}</>
        ) : (
          <>Questo progetto è gestito da <b>{name}</b>. Solo il referente di progetto o un amministratore possono modificare preventivo, outlook e contratto.</>
        )}
      </div>
    </div>
  );
}

const ACTION_OPTIONS = ["chiamata", "sopralluogo", "consulenza", "preventivo", "trattativa", "contratto", "nota"];
const INST_ST_COLORS = { proposto: "#F59E0B", accettato: "#60A5FA", rifiutato: "#EF4444", selezionato: "#34D399", non_selezionato: "#9CA3AF" };
const INST_ST_LABELS = { proposto: "Proposto", accettato: "Ha accettato", rifiutato: "Ha rifiutato", selezionato: "Selezionato", non_selezionato: "Non selezionato" };
const qualityChipColor = (s) => (s >= 90 ? "#10b981" : s >= 70 ? "#84cc16" : s >= 50 ? "#f59e0b" : "#ef4444");

const TIMELINE_META = {
  chiamata: { icon: PhoneCall, color: "#5B8DEF" },
  sopralluogo: { icon: MapPin, color: "#0D9488" },
  consulenza: { icon: MessageSquare, color: "#0EA5A5" },
  preventivo: { icon: FileText, color: "#3B82F6" },
  trattativa: { icon: Handshake, color: "#E08A3C" },
  contratto: { icon: FileSignature, color: "#16A34A" },
  nota: { icon: StickyNote, color: "#6B7280" },
  profilazione: { icon: UserCheck, color: "#8B5CF6" },
  appuntamento: { icon: CalendarClock, color: "#F59E0B" },
  sistema: { icon: Cog, color: "#64748B" },
  apertura: { icon: FolderPlus, color: "#FDD003" },
};

const DIM_META = {
  potenziale: { icon: PiggyBank, color: "#5B8DEF", label: "Potenziale economico" },
  complessita: { icon: Wrench, color: "#E08A3C", label: "Complessità tecnica" },
  probabilita: { icon: Target, color: "#4E9A6B", label: "Probabilità di chiusura" },
};

function levelExplanation(level, b) {
  const compFrac = b.complessita_max ? b.complessita / b.complessita_max : 0;
  const potHigh = b.potenziale_max && b.potenziale >= 0.5 * b.potenziale_max;
  const probHigh = b.probabilita_max && b.probabilita >= 0.5 * b.probabilita_max;
  if (level === 3) {
    const extra = [];
    if (potHigh) extra.push("un elevato potenziale economico");
    if (probHigh) extra.push("una buona probabilità di chiusura");
    let base = "Il progetto presenta una complessità tecnica rilevante";
    if (extra.length) base += `, unita a ${extra.join(" e ")}`;
    return `${base}. Si consiglia il passaggio al Technical Sales per una consulenza dedicata.`;
  }
  if (level === 2) {
    const drivers = [];
    if (potHigh) drivers.push("un buon potenziale economico");
    if (probHigh) drivers.push("una discreta probabilità di chiusura");
    if (b.complessita > 0) drivers.push("una complessità tecnica contenuta");
    const base = drivers.length ? `Il lead presenta ${drivers.join(" e ")}` : "Il lead presenta elementi di interesse";
    return `${base}, ma senza una complessità tecnica tale da richiedere il Technical Sales. Gestione Marketing con il supporto del Technical Sales dove necessario.`;
  }
  return "Lead standard senza particolari complessità tecniche o potenziale elevato. Gestione autonoma da parte del Marketing (nurturing, analisi automatica, pricing standard).";
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [lead, setLead] = useState(null);
  const [config, setConfig] = useState(null);
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState("residenziale");
  const [criteria, setCriteria] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [forcingL3, setForcingL3] = useState(false);
  const [action, setAction] = useState({ type: "chiamata", note: "", date: new Date().toISOString().slice(0, 10) });
  const [qualityWarn, setQualityWarn] = useState(null);
  const [addingAction, setAddingAction] = useState(false);
  const [showInstallers, setShowInstallers] = useState(false);
  const [contact, setContact] = useState({ name: "", role: "" });
  const [cemReason, setCemReason] = useState("");

  const [opportunities, setOpportunities] = useState([]);
  const newOpportunity = async () => {
    try {
      const { data } = await api.post(`/leads/${id}/new-opportunity`);
      toast.success("Nuovo impianto creato: imposta la sede e profila");
      navigate(`/leads/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const load = useCallback(() => api.get(`/leads/${id}`).then((r) => {
    setLead(r.data);
    setTarget(r.data.target || "residenziale");
    setCriteria(r.data.criteria || {});
    setContact({ name: r.data.client_contact_name || "", role: r.data.client_contact_role || "" });
  }), [id]);

  useEffect(() => {
    load();
    api.get("/config").then((r) => setConfig(r.data));
    api.get("/users").then((r) => setUsers(r.data));
    api.get(`/leads/${id}/opportunities`).then((r) => setOpportunities(r.data)).catch(() => {});
  }, [id, load]);

  const toggleHighlight = async (actionId) => {
    try {
      const { data } = await api.post(`/leads/${id}/actions/${actionId}/highlight`);
      setLead((prev) => (prev ? { ...prev, highlighted_action_id: data.highlighted_action_id, highlighted_action: data.lead?.highlighted_action } : prev));
      toast.success(data.highlighted_action_id ? "Azione messa in evidenza" : "Evidenza rimossa");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella messa in evidenza");
    }
  };

  if (!lead || !config) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const isAdmin = ["admin", "team_leader"].includes(currentUser?.role);
  const isProject = lead.level === 3;
  const projectOwnerSet = !!lead.project_owner;
  const canEditProject = !isProject || isAdmin || lead.project_owner === currentUser?.id;
  const canManageOwner = !projectOwnerSet || canEditProject;
  const contactSet = !!(lead.client_contact_name && lead.client_contact_role);
  const reviewed = !!(lead.technical_review && lead.technical_review.reviewed);
  const hasBill = !!(lead.analysis && lead.analysis.pv_kwp);
  const _c = lead?.contract || {};
  const orderConfirmed = !!(_c.ordine_confermato || _c.caparra_pagata || _c.accettazione?.accettato || _c.archived);
  const showIncarico = orderConfirmed && (isAdmin || currentUser?.role === "partner");
  const projectReady = isProject ? (projectOwnerSet && (!hasBill || reviewed)) : true;

  const conf = config.scoring[target];
  const dimEntries = Object.entries(conf.dimensions);
  const breakdown = {};
  dimEntries.forEach(([dk, d]) => {
    breakdown[dk] = d.criteria.reduce((s, c) => s + (criteria[c.key] ? c.points : 0), 0);
    breakdown[`${dk}_max`] = d.criteria.reduce((s, c) => s + c.points, 0);
  });
  const previewScore = (breakdown.potenziale || 0) + (breakdown.complessita || 0) + (breakdown.probabilita || 0);
  const previewMax = (breakdown.potenziale_max || 0) + (breakdown.complessita_max || 0) + (breakdown.probabilita_max || 0);
  const previewLevel = computeLevel(breakdown);
  const previewExplanation = levelExplanation(previewLevel, breakdown);

  const toggle = (key) => setCriteria((c) => ({ ...c, [key]: !c[key] }));

  const changeTarget = (t) => { setTarget(t); setCriteria({}); };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await api.post(`/leads/${id}/profile`, { target, criteria });
      setLead(data);
      toast.success(`Lead profilato — ${LEVEL_META[data.level]?.full || "Livello " + data.level}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const forceLevel3 = async () => {
    setForcingL3(true);
    try {
      const { data } = await api.post(`/leads/${id}/force-level3`);
      setLead(data);
      toast.success("Lead forzato al Livello 3 — ora visibile nei Progetti");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setForcingL3(false);
    }
  };

  const moveStage = async (stage) => {
    const { data } = await api.post(`/leads/${id}/stage`, { stage });
    setLead(data);
    toast.success(`Fase: ${STAGE_LABELS[stage]}`);
  };

  const assign = async (userId) => {
    const { data } = await api.post(`/leads/${id}/assign`, { user_id: userId });
    setLead(data);
    toast.success("Assegnazione aggiornata");
  };

  const setPartnerComm = async (val) => {
    const { data } = await api.put(`/leads/${id}/partner-commerciale`, { partner_commerciale_id: val === "none" ? null : val });
    setLead(data);
    toast.success(val === "none" ? "Partner commerciale rimosso" : "Partner commerciale collegato");
  };

  const setProjectOwner = async (userId) => {
    try {
      const { data } = await api.post(`/leads/${id}/project-owner`, { user_id: userId });
      setLead(data);
      toast.success("Referente di progetto aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveContact = async () => {
    if ((contact.name || "") === (lead.client_contact_name || "") && (contact.role || "") === (lead.client_contact_role || "")) return;
    try {
      const { data } = await api.post(`/leads/${id}/client-contact`, contact);
      setLead(data);
      toast.success("Riferimento cliente salvato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitAction = async (force, quality) => {
    setAddingAction(true);
    try {
      // Usa la data selezionata ma con l'ORA di compilazione (non più le 12:00 fisse)
      let created_at;
      if (action.date) {
        const now = new Date();
        const [y, m, d] = action.date.split("-").map(Number);
        created_at = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds()).toISOString();
      }
      const { data } = await api.post(`/leads/${id}/actions`, { type: action.type, note: action.note, created_at, force: !!force, quality: quality || null });
      if (data.needs_improvement) {
        setQualityWarn({ ...data });
        return;
      }
      setLead(data.lead);
      setQualityWarn(null);
      setAction({ type: "chiamata", note: "", date: new Date().toISOString().slice(0, 10) });
      toast.success("Azione registrata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAddingAction(false);
    }
  };

  const addAction = () => {
    if (!action.note.trim()) { toast.error("Inserisci una descrizione"); return; }
    submitAction(false, null);
  };

  const remove = async () => {
    await api.delete(`/leads/${id}`);
    toast.success("Lead eliminato");
    navigate("/leads");
  };

  const sendToCemetery = async () => {
    try {
      await api.post(`/leads/${id}/cemetery`, { reason: cemReason });
      toast.success("Lead mandato al cimitero");
      navigate("/leads");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const restoreFromCemetery = async () => {
    try {
      const { data } = await api.post(`/leads/${id}/cemetery/restore`);
      setLead(data);
      toast.success("Lead ripristinato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const contractSigned = !!(((lead?.contract) || {}).accettazione || {}).accettato;
  const canDelete = isAdmin || !contractSigned;

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 min-h-16 py-1.5 border-b border-border bg-background flex items-center justify-between gap-2 px-4 lg:px-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground shrink-0" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Indietro
        </button>
        <div className="flex flex-wrap sm:flex-col items-center sm:items-end justify-end gap-1 min-w-0">
          <Button variant="outline" size="sm" onClick={newOpportunity} data-testid="new-opportunity-btn" className="h-7 border-primary/40 text-primary hover:bg-primary/10 mb-0.5" title="Crea un nuovo impianto (altra sede) per lo stesso cliente"><FolderPlus className="h-4 w-4 mr-1.5" /> Nuovo impianto</Button>
          {canDelete ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive h-7" data-testid="delete-lead-btn"><Trash2 className="h-4 w-4 mr-1.5" /> Elimina</Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-popover">
                <AlertDialogHeader><AlertDialogTitle>Eliminare il lead?</AlertDialogTitle><AlertDialogDescription>Questa azione è irreversibile.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} data-testid="confirm-delete-btn" className="bg-destructive text-destructive-foreground">Elimina</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button variant="ghost" size="sm" disabled className="text-muted-foreground h-7 opacity-60 cursor-not-allowed" data-testid="delete-lead-btn" title="Contratto firmato: solo un amministratore può eliminare questo lead."><Lock className="h-4 w-4 mr-1.5" /> Elimina</Button>
          )}
          {lead.cemetery ? (
            <Button variant="ghost" size="sm" onClick={restoreFromCemetery} data-testid="restore-cemetery-btn" className="text-muted-foreground hover:text-foreground h-7"><Undo2 className="h-4 w-4 mr-1.5" /> Ripristina dal cimitero</Button>
          ) : canDelete ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground h-7" data-testid="cemetery-lead-btn"><Skull className="h-4 w-4 mr-1.5" /> Manda al cimitero</Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-popover">
                <AlertDialogHeader><AlertDialogTitle>Mandare il lead al cimitero?</AlertDialogTitle><AlertDialogDescription>Il lead sparirà dalla pipeline e dall'elenco Lead, e resterà visibile solo nella tabella "Cimitero dei lead". Potrai ripristinarlo in qualsiasi momento.</AlertDialogDescription></AlertDialogHeader>
                <div className="py-1">
                  <label className="text-xs text-muted-foreground">Motivo (facoltativo)</label>
                  <Textarea value={cemReason} onChange={(e) => setCemReason(e.target.value)} placeholder="Es. non interessato, irraggiungibile…" data-testid="cemetery-reason-input" className="mt-1" />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={sendToCemetery} data-testid="confirm-cemetery-btn">Manda al cimitero</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button variant="ghost" size="sm" disabled className="text-muted-foreground h-7 opacity-60 cursor-not-allowed" data-testid="cemetery-lead-btn" title="Contratto firmato: solo un amministratore può mandare al cimitero questo lead."><Lock className="h-4 w-4 mr-1.5" /> Manda al cimitero</Button>
          )}
        </div>
      </header>

      <div className="p-4 lg:p-8 grid lg:grid-cols-[340px_minmax(0,1fr)] gap-6 items-start">
        {/* Left info panel */}
        <Card className="rounded border-border p-5 lg:sticky lg:top-24" data-testid="lead-info-panel">
          <h1 className="text-2xl font-heading font-bold leading-tight" data-testid="lead-title">{lead.entity_type === "azienda" ? (lead.ragione_sociale || lead.name) : lead.name}</h1>
          {lead.entity_type === "azienda" && lead.name && lead.name !== lead.ragione_sociale && (
            <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5" data-testid="lead-referente"><UserCheck className="h-3.5 w-3.5" strokeWidth={1.5} /> Referente: {lead.name}</div>
          )}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <LevelBadge level={lead.level} full />
            {lead.level && <span className="text-sm font-heading font-bold">{lead.score} pt</span>}
            {lead.source === "partner_commerciale" && <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-600 text-[11px] font-semibold px-2 py-0.5" data-testid="partner-badge"><Handshake className="h-3 w-3" />Partner commerciale</span>}
            {lead.contract?.commessa && <span className="inline-flex items-center gap-1 rounded-full border border-green-600/40 bg-green-600/10 text-green-700 text-[11px] font-semibold px-2 py-0.5" data-testid="lead-commessa"><Hash className="h-3 w-3" />Commessa {lead.contract.commessa}</span>}
          </div>

          <div className="mt-5 space-y-2.5 text-sm">
            {lead.entity_type === "azienda" ? (
              <>
                {lead.partita_iva && <div className="flex items-center gap-2.5 text-muted-foreground" data-testid="lead-partita-iva"><Hash className="h-4 w-4" strokeWidth={1.5} /> P.IVA {lead.partita_iva}</div>}
              </>
            ) : (
              lead.codice_fiscale && <div className="flex items-center gap-2.5 text-muted-foreground" data-testid="lead-codice-fiscale"><Hash className="h-4 w-4" strokeWidth={1.5} /> CF {lead.codice_fiscale}</div>
            )}
            {lead.phone && <div className="flex items-center gap-2.5 text-muted-foreground"><Phone className="h-4 w-4" strokeWidth={1.5} /> {lead.phone}</div>}
            {lead.email && <div className="flex items-center gap-2.5 text-muted-foreground"><Mail className="h-4 w-4" strokeWidth={1.5} /> {lead.email}</div>}
            {(lead.city || lead.address) && <div className="flex items-center gap-2.5 text-muted-foreground"><MapPin className="h-4 w-4" strokeWidth={1.5} /> {[lead.address, lead.city].filter(Boolean).join(", ")}</div>}
          </div>

          {opportunities.length > 0 && (
            <div className="mt-5 pt-5 border-t border-border" data-testid="lead-impianti">
              <label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><FolderPlus className="h-3.5 w-3.5 text-primary" /> Impianti del cliente</label>
              <div className="mt-2 space-y-1.5">
                {[{ id: lead.id, city: lead.city, address: lead.address, stage: lead.stage, current: true }, ...opportunities].map((o) => (
                  <button key={o.id} onClick={() => { if (!o.current) navigate(`/leads/${o.id}`); }} data-testid={`impianto-link-${o.id}`} className={`w-full text-left rounded-md border px-2.5 py-1.5 transition-colors flex items-center justify-between gap-2 ${o.current ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>
                    <span className="text-sm truncate flex items-center gap-1.5">{o.current && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}{[o.address, o.city].filter(Boolean).join(", ") || "Sede da definire"}</span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">{o.current ? "attuale" : (STAGE_LABELS[o.stage] || o.stage)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 pt-5 border-t border-border grid grid-cols-2 gap-y-4 text-sm">
            <Meta label="Origine" value={SOURCE_LABELS[lead.source]} />
            {lead.attribution?.canale && <Meta label="Arrivato da" value={`${lead.attribution.canale}${lead.attribution.a_pagamento ? " Ads" : ""}${lead.attribution.utm_campaign ? ` · ${lead.attribution.utm_campaign}` : ""}`} />}
            <Meta label="Target" value={TARGET_LABELS[lead.target]} />
            <Meta label="Creato da" value={lead.created_by_name || "—"} />
          </div>

          <div className="mt-5 pt-5 border-t border-border space-y-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Fase</label>
              {isAdmin && !orderConfirmed ? (
                <>
                  <Select value={lead.stage} onValueChange={moveStage}>
                    <SelectTrigger className="mt-1.5" data-testid="stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="stage-admin-note">
                    Fase automatica. Correzione manuale riservata all'Amministratore.
                  </p>
                </>
              ) : (
                <>
                  <div className="mt-1.5 inline-flex items-center rounded-md border border-border px-3 py-2 text-sm font-medium" data-testid="stage-badge">
                    {STAGE_LABELS[lead.stage] || lead.stage}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="stage-auto-note">
                    {orderConfirmed
                      ? "Fase gestita in automatico: l'ordine è confermato (contratto firmato)."
                      : "La fase avanza in automatico in base agli step del flusso."}
                  </p>
                </>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Assegnato a *</label>
              <Select value={lead.assigned_to || ""} onValueChange={assign}>
                <SelectTrigger className="mt-1.5" data-testid="assign-select"><SelectValue placeholder="Seleziona un referente" /></SelectTrigger>
                <SelectContent>
                  {users.filter((u) => u.role !== "admin" || u.id === lead.assigned_to).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Handshake className="h-3 w-3 text-amber-600" /> Partner commerciale</label>
              <Select value={lead.partner_commerciale_id || "none"} onValueChange={setPartnerComm}>
                <SelectTrigger className="mt-1.5" data-testid="sidebar-partner-comm-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {users.filter((u) => u.role === "partner_commerciale").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {lead.partner_commerciale_name && <p className="text-[10px] text-muted-foreground mt-1" data-testid="sidebar-partner-comm-info">Provvigione applicata: {lead.partner_commission_pct != null ? `${lead.partner_commission_pct}%` : "—"}</p>}
            </div>
            {isProject && (
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Rocket className="h-3 w-3 text-primary" /> Referente di progetto {!projectOwnerSet && <span className="text-primary">*</span>}</label>
              {canManageOwner ? (
                <Select value={lead.project_owner || ""} onValueChange={setProjectOwner}>
                  <SelectTrigger className="mt-1.5" data-testid="project-owner-select"><SelectValue placeholder="Seleziona chi prende in mano il progetto" /></SelectTrigger>
                  <SelectContent>
                    {users.filter((u) => u.role !== "admin" || u.id === lead.assigned_to).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <div className="mt-1.5 text-sm font-medium flex items-center gap-2" data-testid="project-owner-readonly">
                  <UserCheck className="h-4 w-4 text-primary" /> {lead.project_owner_name}
                </div>
              )}
              <p className="text-[10px] text-muted-foreground mt-1">
                {projectOwnerSet
                  ? "Solo il referente di progetto o un amministratore possono sviluppare preventivo, outlook e contratto."
                  : "Obbligatorio prima di generare il preventivo. Chi lo imposta ne diventa il responsabile."}
              </p>
            </div>
            )}
            {isProject && (
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><UserCheck className="h-3 w-3 text-primary" /> Riferimento cliente</label>
              {canManageOwner ? (
                <div className="mt-1.5 space-y-2">
                  <Input value={contact.name} onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))} onBlur={saveContact} placeholder="Persona di riferimento" data-testid="client-contact-name" />
                  <Input value={contact.role} onChange={(e) => setContact((c) => ({ ...c, role: e.target.value }))} onBlur={saveContact} placeholder="Ruolo in azienda" data-testid="client-contact-role" />
                </div>
              ) : (
                <div className="mt-1.5 text-sm" data-testid="client-contact-readonly">
                  {lead.client_contact_name ? <span className="font-medium">{lead.client_contact_name}</span> : <span className="text-muted-foreground">—</span>}
                  {lead.client_contact_role && <span className="text-muted-foreground"> · {lead.client_contact_role}</span>}
                </div>
              )}
              <p className="text-[10px] text-muted-foreground mt-1">Persona e ruolo nell'azienda cliente. Facoltativi.</p>
            </div>
            )}
          </div>

          {lead.notes && <div className="mt-5 pt-5 border-t border-border"><div className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Note</div><p className="text-sm text-foreground/80">{lead.notes}</p></div>}

          {lead.partner_ref?.via_partner && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2" data-testid="lead-partner-ref">
              {lead.partner_ref.partner_logo_url ? <img src={lead.partner_ref.partner_logo_url} alt="" className="h-7 w-7 rounded object-contain bg-white border border-border" /> : <Handshake className="h-4 w-4 text-amber-600" />}
              <div className="text-xs leading-tight"><span className="text-muted-foreground">Arrivato tramite partner</span><div className="font-semibold text-sm">{lead.partner_ref.partner_name || "Partner"}</div></div>
            </div>
          )}
          {lead.cliente_id && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2" data-testid="lead-linked-cliente">
              <Contact className="h-4 w-4 text-primary" />
              <div className="text-xs leading-tight flex-1"><span className="text-muted-foreground">Presente in Anagrafica</span></div>
              <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => navigate(`/clienti/${lead.cliente_id}`)} data-testid="lead-open-cliente">Apri scheda</Button>
            </div>
          )}
          <AutomationLeadPanel leadId={lead.id} />
          <PrivacyLeadPanel leadId={lead.id} isAdmin={currentUser?.role === "admin"} />

          <div className="mt-5 pt-5 border-t border-border" data-testid="lead-installers-card">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><HardHat className="h-3 w-3 text-primary" /> Installatori in gara</div>
              <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setShowInstallers(true)} data-testid="open-installers-dialog">
                <UserPlus className="h-3.5 w-3.5 mr-1" /> Gestisci
              </Button>
            </div>
            {(lead.assigned_installers || []).length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="installers-empty">Nessun installatore attivato per questo lead.</p>
            ) : (
              <div className="space-y-1.5" data-testid="installers-list">
                {(lead.assigned_installers || []).map((i) => (
                  <div key={i.installer_id} className="flex items-center justify-between gap-2 text-sm" data-testid={`installer-item-${i.installer_id}`}>
                    <span className="truncate">{i.ragione_sociale}</span>
                    <Badge variant="outline" className="text-[10px] font-normal shrink-0" style={{ borderColor: (INST_ST_COLORS[i.status] || "#9CA3AF") + "66", color: INST_ST_COLORS[i.status] || "#9CA3AF" }}>
                      {INST_ST_LABELS[i.status] || i.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Right tabs */}
        <div className="min-w-0">
          <Tabs defaultValue="azioni">
            <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start h-auto p-0 gap-6 overflow-x-auto no-scrollbar">
              <TabsTrigger value="azioni" data-testid="tab-azioni" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary px-0 pb-3">Azioni & Timeline</TabsTrigger>
              <TabsTrigger value="profilazione" data-testid="tab-profilazione" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary px-0 pb-3">Profilazione</TabsTrigger>
              <TabsTrigger value="consulenza" data-testid="tab-consulenza" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary px-0 pb-3">Consulenza</TabsTrigger>
              {isProject && <TabsTrigger value="revisione" data-testid="tab-revisione" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary px-0 pb-3">Revisione</TabsTrigger>}
              <TabsTrigger value="preventivo" data-testid="tab-preventivo" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary px-0 pb-3">Preventivo</TabsTrigger>
              <TabsTrigger value="outlook" data-testid="tab-outlook" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary px-0 pb-3">Outlook</TabsTrigger>
              {showIncarico && <TabsTrigger value="incarico" data-testid="tab-incarico" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary px-0 pb-3">Incarico Installatore</TabsTrigger>}
            </TabsList>

            {/* Profilazione */}
            <TabsContent value="profilazione" className="mt-6 space-y-5">
              <LeadAnagrafica lead={lead} onUpdate={setLead} />
              <Card className="rounded border-border p-5">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="font-heading font-semibold">Profilazione — Livello di consulenza</h2>
                  <Select value={target} onValueChange={changeTarget}>
                    <SelectTrigger className="w-[220px]" data-testid="profile-target-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(config.scoring).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground mb-5">Valuta quanto valore aggiunge una consulenza tecnico-commerciale: più il progetto è complesso e ad alto potenziale, più sale il livello.</p>

                <div className="space-y-6">
                  {dimEntries.map(([dk, d]) => {
                    const M = DIM_META[dk] || {};
                    const Icon = M.icon;
                    return (
                      <div key={dk} data-testid={`dim-${dk}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {Icon && <Icon className="h-4 w-4" style={{ color: M.color }} strokeWidth={1.5} />}
                            <span className="text-sm font-semibold">{d.label}</span>
                          </div>
                          <span className="text-xs font-semibold text-muted-foreground">{breakdown[dk]}/{breakdown[`${dk}_max`]} pt</span>
                        </div>
                        <div className="space-y-1">
                          {d.criteria.map((c) => (
                            <label key={c.key} data-testid={`criterion-${c.key}`} className="flex items-center justify-between py-2 px-3 rounded hover:bg-secondary/50 cursor-pointer transition-colors">
                              <div className="flex items-center gap-3">
                                <Checkbox checked={!!criteria[c.key]} onCheckedChange={() => toggle(c.key)} data-testid={`check-${c.key}`} />
                                <span className="text-sm">{c.label}</span>
                              </div>
                              <span className="text-xs font-semibold text-muted-foreground">+{c.points}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 pt-5 border-t border-border">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">Livello di consulenza consigliato</div>
                      <div className="mt-1.5"><LevelBadge level={previewLevel} full data-testid="preview-level" /></div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">Punteggio complessivo</div>
                      <div className="font-heading font-bold text-2xl" data-testid="preview-score">{previewScore}<span className="text-sm text-muted-foreground font-normal"> / {previewMax}</span></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {dimEntries.map(([dk, d]) => {
                      const M = DIM_META[dk] || {};
                      const max = breakdown[`${dk}_max`] || 1;
                      const pct = Math.round((breakdown[dk] / max) * 100);
                      return (
                        <div key={dk}>
                          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground mb-1"><span>{M.label?.split(" ")[0]}</span><span>{breakdown[dk]}/{max}</span></div>
                          <div className="h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: M.color }} /></div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="rounded border border-border bg-secondary/20 p-3 text-sm text-foreground/80" data-testid="preview-explanation">{previewExplanation}</div>
                  <Button onClick={saveProfile} disabled={savingProfile} className="w-full mt-4 font-semibold" data-testid="save-profile-btn">
                    {savingProfile ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva profilazione
                  </Button>
                  <Button variant="outline" onClick={forceLevel3} disabled={forcingL3} className="w-full mt-2 font-semibold border-red-500/50 text-red-600 hover:bg-red-500/10" data-testid="force-level3-btn">
                    {forcingL3 ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Forza Livello 3 (Progetto)
                  </Button>
                  {lead.level_forced && <p className="text-[11px] text-red-600/80 mt-1.5 text-center" data-testid="level-forced-note">Livello 3 forzato manualmente — visibile nei Progetti.</p>}
                </div>
              </Card>

              <PreFattibilita lead={lead} onUpdate={setLead} />
            </TabsContent>

            {/* Consulenza (dall'inserimento bolletta in poi) */}
            <TabsContent value="consulenza" className="mt-6 space-y-5">
              <LeadAttachments lead={lead} onUpdate={setLead} />

              <RoofMap lead={lead} onUpdate={setLead} />

              <LeadAnalysis lead={lead} onUpdate={setLead} />

              <EvBenefitEditor lead={lead} onUpdate={setLead} />

              <PreOutlookShare leadId={lead.id} lead={lead} defaultEmail={lead.email} />
            </TabsContent>

            {/* Azioni */}
            <TabsContent value="azioni" className="mt-6 space-y-5">
              <Card className="rounded border-border p-5">
                <h2 className="font-heading font-semibold mb-4">Registra azione</h2>
                <div className="flex flex-wrap gap-3">
                  <Select value={action.type} onValueChange={(v) => setAction((a) => ({ ...a, type: v }))}>
                    <SelectTrigger className="w-[180px]" data-testid="action-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{ACTION_OPTIONS.map((t) => <SelectItem key={t} value={t}>{ACTION_LABELS[t]}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="space-y-1">
                    <Input type="date" value={action.date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setAction((a) => ({ ...a, date: e.target.value }))} className="w-[180px]" data-testid="action-date-input" />
                  </div>
                </div>
                <Textarea value={action.note} onChange={(e) => setAction((a) => ({ ...a, note: e.target.value }))} placeholder="Descrizione dell'azione, esito, prossimi passi..." rows={3} className="mt-3" data-testid="action-note-input" />
                <Button onClick={addAction} disabled={addingAction} className="mt-3 font-semibold" data-testid="add-action-btn">
                  {addingAction ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />} Aggiungi
                </Button>
              </Card>

              <QualityWarningDialog data={qualityWarn} saving={addingAction} onModifica={() => setQualityWarn(null)} onSalvaComunque={() => submitAction(true, qualityWarn?.quality)} />

              <Card className="rounded border-border p-5">
                <h2 className="font-heading font-semibold mb-4">Timeline</h2>
                {(() => {
                  const entries = [...(lead.timeline || [])];
                  if (lead.created_at) {
                    entries.push({
                      id: "__apertura__",
                      type: "apertura",
                      note: `Lead aperto${lead.source ? ` · fonte ${SOURCE_LABELS[lead.source] || lead.source}` : ""}.`,
                      created_at: lead.created_at,
                      created_by_name: lead.created_by_name,
                    });
                  }
                  entries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                  return entries.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">Nessuna azione registrata.</p>
                  ) : (
                    <div className="space-y-4">
                      {entries.map((a) => {
                        const meta = TIMELINE_META[a.type] || { icon: Zap, color: "#64748B" };
                        const Icon = meta.icon;
                        const label = a.type === "apertura" ? "Apertura lead" : (ACTION_LABELS[a.type] || a.type);
                        return (
                        <div key={a.id} className="flex gap-3" data-testid={`timeline-${a.id}`}>
                          <div className="mt-0.5 h-8 w-8 shrink-0 rounded flex items-center justify-center" style={{ backgroundColor: `${meta.color}1A` }} data-testid={`timeline-icon-${a.type}`}>
                            <Icon className="h-4 w-4" strokeWidth={1.5} style={{ color: meta.color }} />
                          </div>
                          <div className="flex-1 border-b border-border pb-4">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold">{label}</span>
                              <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(a.created_at).toLocaleString("it-IT")}</span>
                            </div>
                            {a.note && <p className="text-sm text-muted-foreground mt-1">{a.note}</p>}
                            {a.quality && a.quality.score != null && (
                              <span className="inline-flex items-center gap-1 mt-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold" style={{ borderColor: `${qualityChipColor(a.quality.score)}66`, color: qualityChipColor(a.quality.score) }} data-testid={`timeline-quality-${a.id}`}>
                                Qualità {a.quality.score}/100{a.quality.overridden ? " · salvato comunque" : ""}
                              </span>
                            )}
                            {a.created_by_name && <span className="text-xs text-muted-foreground">— {a.created_by_name}</span>}
                            {a.id !== "__apertura__" && (
                              <div className="mt-1.5">
                                <button onClick={() => toggleHighlight(a.id)} data-testid={`timeline-highlight-${a.id}`}
                                  className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${lead.highlighted_action_id === a.id ? "bg-amber-500/15 text-amber-600 border-amber-500/40" : "border-border text-muted-foreground hover:text-amber-600 hover:border-amber-500/40"}`}>
                                  <Star className={`h-3 w-3 ${lead.highlighted_action_id === a.id ? "fill-amber-500 text-amber-500" : ""}`} /> {lead.highlighted_action_id === a.id ? "In evidenza" : "Metti in evidenza"}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </Card>
            </TabsContent>

            {isProject && (
            <TabsContent value="revisione" className="mt-6">
              <TechnicalReview lead={lead} onUpdate={setLead} canEdit={canEditProject} projectOwnerSet={projectOwnerSet} />
            </TabsContent>
            )}
            <TabsContent value="preventivo" className="mt-6">
              {!projectReady ? (
                <ProjectGate variant="required" isProject={isProject} ownerSet={projectOwnerSet} contactSet={contactSet} reviewed={reviewed} />
              ) : (
                <>
                  {isProject && !canEditProject && <ProjectGate variant="locked" name={lead.project_owner_name} />}
                  <LeadPreventivo lead={lead} onUpdate={setLead} canEdit={canEditProject} />
                </>
              )}
            </TabsContent>
            <TabsContent value="outlook" className="mt-6">
              {!projectReady ? (
                <ProjectGate variant="required" isProject={isProject} ownerSet={projectOwnerSet} contactSet={contactSet} reviewed={reviewed} />
              ) : (
                <>
                  {isProject && !canEditProject && <ProjectGate variant="locked" name={lead.project_owner_name} />}
                  <LeadOutlook lead={lead} />
                </>
              )}
            </TabsContent>

            {showIncarico && (
              <TabsContent value="incarico" className="mt-6">
                <PartnerAssignment lead={lead} />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      <AssignInstallersDialog
        lead={lead}
        open={showInstallers}
        onOpenChange={setShowInstallers}
        onChanged={load}
      />
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}
